import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { likePost, unlikePost } from '../api/community';
import { followAuthor, unfollowAuthor } from '../api/discover';
import {
  forgetPost,
  getPost,
  keepPost,
  listVersions,
  renderPost,
  restoreVersion,
  unpublishPost,
  type Post,
  type RenderedStoryBlock,
  type StoryVersion,
} from '../api/posts';
import type { Visibility } from '../api/visibility';
import { VisibilityControl } from '../components/VisibilityControl';
import { StoryBody } from '../components/posts/StoryBody';
import { ModerationNotice } from '../components/posts/ModerationNotice';
import { ReportControl } from '../components/posts/ReportControl';
import { StoryComments } from '../components/posts/StoryComments';
import { StoryStanding } from '../components/posts/StoryStanding';
import { StoryVersions } from '../components/posts/StoryVersions';
import { useAuth } from '../auth/useAuth';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, ErrorState, LoadingState } from '../components/ui';
import './StoryPage.css';

type State = 'loading' | 'ready' | 'error';

/**
 * `/stories/:id` — the story, read.
 *
 * With no `?version=` this is what a reader is served: the published version if
 * there is one. Any earlier version can be named, and reads exactly as it read
 * then — that is what keeping archived versions is for.
 */
export function StoryPage() {
  const { postId = '' } = useParams();
  const { account } = useAuth();
  const [params, setParams] = useSearchParams();
  const asked = params.get('version');

  const [state, setState] = useState<State>('loading');
  const [post, setPost] = useState<Post | null>(null);
  const [versions, setVersions] = useState<StoryVersion[]>([]);
  const [rendered, setRendered] = useState<Record<string, RenderedStoryBlock>>({});
  const [working, setWorking] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const number = asked ? Number(asked) : undefined;
      const loaded = await getPost(postId, number);
      setPost(loaded);
      setState('ready');

      // A reader sees the story, not its history: the versions are the
      // author's working record, and asking for them as anyone else is a 404.
      setVersions(loaded.is_mine ? await listVersions(postId) : []);

      const answer = await renderPost(loaded.id, loaded.version?.number);
      setRendered(Object.fromEntries(answer.blocks.map((block) => [block.id, block])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [postId, asked]);

  useEffect(() => {
    void load();
  }, [load]);

  const read = (number: number) => {
    setRendered({});
    setParams({ version: String(number) });
  };

  const run = async (action: () => Promise<unknown>, marker: number) => {
    setWorking(marker);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(null);
    }
  };

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Opening the story…" />
      </AppShell>
    );
  }

  if (state === 'error' || !post) {
    return (
      <AppShell>
        <ErrorState
          title="We could not open this story"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  const showing = post.version?.number;
  const live = post.published_version;

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            {post.is_mine ? (
              <StoryStanding post={post} />
            ) : (
              <p className="sc-story__kicker">
                {post.author.display_name === post.author.handle
                  ? `@${post.author.handle}`
                  : `${post.author.display_name} · @${post.author.handle}`}
                {post.category && ` · ${post.category.name}`}
              </p>
            )}
          </div>
          <div className="sc-page-header__actions">
            {post.is_mine && <Link to="/stories">All stories</Link>}
            {account && (
              <Button
                variant="secondary"
                size="sm"
                isLoading={working === -2}
                onClick={() =>
                  void run(() => (post.is_favorite ? forgetPost(post.id) : keepPost(post.id)), -2)
                }
              >
                {post.is_favorite ? 'Saved' : 'Save'}
              </Button>
            )}
            {/* `can_like` rather than `!is_mine`: the backend decides, and an
                interface that offers what would be refused is not believed
                twice (Rule 7). */}
            {post.can_like && (
              <Button
                variant="secondary"
                size="sm"
                isLoading={working === -3}
                onClick={() =>
                  void run(() => (post.is_liked ? unlikePost(post.id) : likePost(post.id)), -3)
                }
              >
                {/* The action, not the number: the count is on the reception
                    line below, where the author reads it too. A live run had
                    both, which is one place too many for one figure. */}
                {post.is_liked ? 'Liked' : 'Like'}
              </Button>
            )}
            {!post.is_mine && account && (
              <Button
                variant={post.is_following_author ? 'secondary' : 'primary'}
                size="sm"
                isLoading={working === -1}
                onClick={() =>
                  void run(
                    () =>
                      post.is_following_author
                        ? unfollowAuthor(post.author.handle)
                        : followAuthor(post.author.handle),
                    -1,
                  )
                }
              >
                {post.is_following_author
                  ? `Following ${post.author.display_name}`
                  : `Follow ${post.author.display_name}`}
              </Button>
            )}
            {post.is_mine && live !== null && (
              <Button
                variant="secondary"
                size="sm"
                isLoading={working === 0}
                onClick={() => void run(() => unpublishPost(post.id), 0)}
              >
                Take it down
              </Button>
            )}
            {post.is_mine && (
              <Link to={`/stories/${post.id}/edit`}>
                <Button variant="secondary" size="sm">
                  Edit
                </Button>
              </Link>
            )}
          </div>
        </div>
      }
    >
      <ModerationNotice post={post} />

      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      {showing !== undefined && live !== null && showing !== live && (
        <Alert tone="info" title={`You are reading version ${showing}`}>
          Version {live} is the one people read. This one is kept as it was.
        </Alert>
      )}

      <StoryBody post={post} rendered={rendered} />

      {/* How it was received, for everyone including its author — who is the
          person most likely to want to know, and who never gets the Like
          button. Likes and readers only: the comment count is the heading of
          the section immediately below, and saying it twice would be noise. */}
      {live !== null && (
        <p className="sc-story__measure sc-story__reception">
          {post.like_count === 1 ? '1 like' : `${post.like_count} likes`}
          {' · '}
          {post.view_count === 1 ? '1 reader' : `${post.view_count} readers`}
        </p>
      )}

      {!post.is_mine && post.tags.length > 0 && (
        <p className="sc-story__measure sc-story__topics">
          {post.tags.map((tag) => (
            <Link key={tag.slug} to={`/discover?tag=${tag.slug}`}>
              #{tag.name}
            </Link>
          ))}
        </p>
      )}

      {post.is_mine && (
        <VisibilityControl
          kind="posts"
          id={post.id}
          value={post.visibility}
          noun="story"
          onChanged={(visibility: Visibility) => setPost({ ...post, visibility })}
        />
      )}

      {/* Not on your own story: there is nothing to tell the instance about
          your own work, and the backend refuses it anyway. */}
      {!post.is_mine && account && (
        <div className="sc-story__measure">
          <ReportControl postId={post.id} />
        </div>
      )}

      {/* The conversation, below the story and quieter than it. Only where
          there is a published story to talk about: a draft nobody has read is
          not something anybody has said anything about. */}
      {live !== null && (
        <div className="sc-story__measure">
          <StoryComments post={post} />
        </div>
      )}

      {post.is_mine && (
        <StoryVersions
          versions={versions}
          current={showing}
          busy={working}
          onRead={read}
          onRestore={(number) => void run(() => restoreVersion(post.id, number), number)}
        />
      )}
    </AppShell>
  );
}
